"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.GCodeRenderer = void 0;
const three_1 = require("three");
const OrbitControls_1 = require("three/examples/jsm/controls/OrbitControls");
const LineTubeGeometry_1 = require("./LineTubeGeometry");
const LinePoint_1 = require("./LinePoint");
const SegmentColorizer_1 = require("./SegmentColorizer");
/**
 * GCode rendere which parses a GCode file and displays it using
 * three.js. Use .element() to retrieve the DOM canvas element.
 */
class GCodeRenderer {
    /**
     * Creates a new GCode renderer for the given gcode.
     * It initializes the canvas to the given size and
     * uses the passed color as background.
     *
     * @param {string} gCode
     * @param {number} width
     * @param {number} height
     * @param {Color} background
     */
    constructor(gCode, width, height, background) {
        this.lineMaterial = new three_1.MeshPhongMaterial({ vertexColors: true });
        this.minTemp = undefined;
        this.maxTemp = 0;
        this.minSpeed = undefined;
        this.maxSpeed = 0;
        this.layerIndex = [];
        // Public configurations: 
        /**
         * Width of travel-lines. Use 0 to hide them.
         *
         * @type number
         */
        this.travelWidth = 0.01;
        /**
         * Set any colorizer implementation to change the segment color based on the segment
         * metadata. Some default implementations are provided.
         *
         * @type SegmentColorizer
         */
        this.colorizer = new SegmentColorizer_1.SimpleColorizer();
        /**
         * The number of radial segments per line.
         * Less (e.g. 3) provides faster rendering with less memory usage.
         * More (e.g. 8) provides a better look.
         *
         * @default 8
         * @type number
         */
        this.radialSegments = 8;
        this.scene = new three_1.Scene();
        this.renderer = new three_1.WebGLRenderer();
        this.renderer.setSize(width, height);
        this.renderer.setClearColor(background, 1);
        this.camera = this.newCamera(width, height);
        this.gCode = gCode;
        // Pre-calculate some min max values, needed for colorizing.
        this.calcMinMaxMetadata();
    }
    /**
     * This can be used to retrieve some min / max values which may
     * be needed as param for a colorizer.
     * @returns {{
     *         minTemp: number | undefined,
     *         maxTemp: number,
     *         minSpeed: number | undefined,
     *         maxSpeed: number
     *     }}
     */
    getMinMaxValues() {
        return {
            minTemp: this.minTemp,
            maxTemp: this.maxTemp,
            minSpeed: this.minSpeed,
            maxSpeed: this.maxSpeed
        };
    }
    newCamera(width, height) {
        var _a;
        const camera = new three_1.PerspectiveCamera(75, width / height, 0.1, 1000);
        if (this.cameraControl) {
            this.cameraControl.dispose();
        }
        this.cameraControl = new OrbitControls_1.OrbitControls(camera, this.renderer.domElement);
        this.cameraControl.enablePan = true;
        this.cameraControl.enableZoom = true;
        this.cameraControl.minDistance = -Infinity;
        this.cameraControl.maxDistance = Infinity;
        (_a = this.cameraControl) === null || _a === void 0 ? void 0 : _a.addEventListener("change", () => {
            requestAnimationFrame(() => this.draw());
        });
        return camera;
    }
    fitCamera() {
        var _a, _b, _c, _d;
        const boundingBox = new three_1.Box3(this.min, this.max);
        const center = new three_1.Vector3();
        boundingBox.getCenter(center);
        this.camera.position.x = (((_a = this.min) === null || _a === void 0 ? void 0 : _a.x) || 0) + ((((_b = this.max) === null || _b === void 0 ? void 0 : _b.x) || 0) - (((_c = this.min) === null || _c === void 0 ? void 0 : _c.x) || 0)) / 2;
        this.camera.position.z = (((_d = this.max) === null || _d === void 0 ? void 0 : _d.z) || 0);
        if (this.cameraControl) {
            this.cameraControl.target = center;
        }
        this.camera.lookAt(center);
        this.draw();
    }
    /**
     * Recalculate the bounding box with the new point.
     * @param {Vector3} newPoint
     */
    calcMinMax(newPoint) {
        if (this.min === undefined) {
            this.min = newPoint.clone();
        }
        if (this.max === undefined) {
            this.max = newPoint.clone();
        }
        if (newPoint.x > this.max.x) {
            this.max.x = newPoint.x;
        }
        if (newPoint.y > this.max.y) {
            this.max.y = newPoint.y;
        }
        if (newPoint.z > this.max.z) {
            this.max.z = newPoint.z;
        }
        if (newPoint.x < this.min.x) {
            this.min.x = newPoint.x;
        }
        if (newPoint.y < this.min.y) {
            this.min.y = newPoint.y;
        }
        if (newPoint.z < this.min.z) {
            this.min.z = newPoint.z;
        }
    }
    parseValue(value) {
        if (!value) {
            return undefined;
        }
        return Number.parseFloat(value.substring(1));
    }
    /**
     * Pre-calculates the min max metadata which may be needed for the colorizers.
     */
    calcMinMaxMetadata() {
        this.gCode.split("\n").forEach((line, i) => {
            if (line === undefined || line[0] === ";") {
                return;
            }
            const cmd = line.split(" ");
            if (cmd[0] === "G0" || cmd[0] === "G1") {
                // Feed rate -> speed
                const f = this.parseValue(cmd.find((v) => v[0] === "F"));
                if (f === undefined) {
                    return;
                }
                if (f > this.maxSpeed) {
                    this.maxSpeed = f;
                }
                if (this.minSpeed === undefined || f < this.minSpeed) {
                    this.minSpeed = f;
                }
            }
            else if (cmd[0] === "M104" || cmd[0] === "M109") {
                // hot end temperature
                // M104 S205 ; set hot end temp
                // M109 S205 ; wait for hot end temp
                const hotendTemp = this.parseValue(cmd.find((v) => v[0] === "S")) || 0;
                if (hotendTemp > this.maxTemp) {
                    this.maxTemp = hotendTemp;
                }
                if (this.minTemp === undefined || hotendTemp < this.minTemp) {
                    this.minTemp = hotendTemp;
                }
            }
        });
    }
    /**
     * Reads the GCode and renders it to a mesh.
     */
    render() {
        return __awaiter(this, void 0, void 0, function* () {
            // Cache the start and end of each layer.
            // Note: This may not work properly in some cases where the nozzle moves back down mid-print.
            const layerPointsCache = new Map();
            // Remember which values are in relative-mode.
            const relative = {
                x: false, y: false, z: false, e: false
            };
            // Save some values
            let lastPoint = new three_1.Vector3(0, 0, 0);
            let lastE = 0;
            let lastF = 0;
            let hotendTemp = 0;
            // Retrieves a value taking into account possible relative values.
            const getValue = (cmd, name, last, relative) => {
                let val = this.parseValue(cmd.find((v) => v[0] === name));
                if (val !== undefined) {
                    if (relative) {
                        val += last;
                    }
                }
                else {
                    val = last;
                }
                return val;
            };
            // Create the geometry.
            this.combinedLine = new LineTubeGeometry_1.LineTubeGeometry(this.radialSegments);
            const lines = this.gCode.split("\n");
            this.gCode = ""; // clear memory
            lines.forEach((line, i) => {
                if (this.combinedLine === undefined || line === undefined) {
                    return;
                }
                // Split off comments.
                line = line.split(";", 2)[0];
                const cmd = line.split(" ");
                // A move command.
                if (cmd[0] === "G0" || cmd[0] === "G1") {
                    const x = getValue(cmd, "X", lastPoint.x, relative.x);
                    const y = getValue(cmd, "Y", lastPoint.y, relative.y);
                    const z = getValue(cmd, "Z", lastPoint.z, relative.z);
                    const e = getValue(cmd, "E", lastE, relative.e);
                    const f = this.parseValue(cmd.find((v) => v[0] === "F")) || lastF;
                    const newPoint = new three_1.Vector3(x, y, z);
                    const curve = new three_1.LineCurve3(lastPoint, newPoint);
                    const length = curve.getLength();
                    // TODO: why are there some with length 0? Is it an error in GoSlice??
                    if (length !== 0) {
                        let radius = (e - lastE) / length * 10;
                        if (radius == 0) {
                            radius = this.travelWidth;
                        }
                        else {
                            // Update the bounding box.
                            this.calcMinMax(newPoint);
                        }
                        // Get the color for this line.
                        const color = this.colorizer.getColor({
                            radius,
                            segmentStart: lastPoint,
                            segmentEnd: newPoint,
                            speed: f,
                            temp: hotendTemp
                        });
                        // Insert the last point with the current radius.
                        // As the GCode contains the extrusion for the 'current' line, 
                        // but the LinePoint contains the radius for the 'next' line
                        // we need to combine the last point with the current radius.
                        this.combinedLine.add(new LinePoint_1.LinePoint(lastPoint.clone(), radius, color));
                        // Try to figure out the layer start and end points.
                        if (lastPoint.z !== newPoint.z) {
                            let last = layerPointsCache.get(lastPoint.z);
                            let current = layerPointsCache.get(newPoint.z);
                            if (last === undefined) {
                                last = {
                                    end: 0,
                                    start: 0
                                };
                            }
                            if (current === undefined) {
                                current = {
                                    end: 0,
                                    start: 0
                                };
                            }
                            last.end = this.combinedLine.pointsCount() - 1;
                            current.start = this.combinedLine.pointsCount();
                            layerPointsCache.set(lastPoint.z, last);
                            layerPointsCache.set(newPoint.z, current);
                        }
                    }
                    // Save the data.
                    lastPoint.copy(newPoint);
                    lastE = e;
                    lastF = f;
                    // Set a value directly.
                }
                else if (cmd[0] === "G92") {
                    // set state
                    lastPoint = new three_1.Vector3(this.parseValue(cmd.find((v) => v[0] === "X")) || lastPoint.x, this.parseValue(cmd.find((v) => v[0] === "Y")) || lastPoint.y, this.parseValue(cmd.find((v) => v[0] === "Z")) || lastPoint.z);
                    lastE = this.parseValue(cmd.find((v) => v[0] === "E")) || lastE;
                    // Hot end temperature.
                }
                else if (cmd[0] === "M104" || cmd[0] === "M109") {
                    // M104 S205 ; start heating hot end
                    // M109 S205 ; wait for hot end temperature
                    hotendTemp = this.parseValue(cmd.find((v) => v[0] === "S")) || 0;
                }
                lines[i] = undefined;
            });
            // Calculate the mesh.
            this.combinedLine.finish();
            this.scene.add(new three_1.Mesh(this.combinedLine, this.lineMaterial));
            // Sort the layers by starting line number.
            this.layerIndex = Array.from(layerPointsCache.values()).sort((v1, v2) => v1.start - v2.start);
            // Set up some lights.
            const ambientLight = new three_1.AmbientLight(0xffffff, 0.5);
            this.scene.add(ambientLight);
            const spotLight = new three_1.SpotLight(0xffffff, 0.9);
            spotLight.position.set(200, 400, 300);
            spotLight.lookAt(new three_1.Vector3(0, 0, 0));
            const spotLight2 = new three_1.SpotLight(0xffffff, 0.9);
            spotLight2.position.set(-200, -400, -300);
            spotLight2.lookAt(new three_1.Vector3(0, 0, 0));
            this.scene.add(spotLight);
            this.scene.add(spotLight2);
            this.fitCamera();
        });
    }
    draw() {
        if (this.combinedLine === undefined || this.camera === undefined) {
            return;
        }
        this.renderer.render(this.scene, this.camera);
    }
    /**
     * Slices the rendered model based on the passed start and end line numbers.
     * (0, pointsCount()) renders everything
     *
     * Note: Currently negative values are not allowed.
     *
     * @param {number} start the starting segment
     * @param {number} end the ending segment (excluding)
     */
    slice(start, end) {
        var _a;
        (_a = this.combinedLine) === null || _a === void 0 ? void 0 : _a.slice(start, end);
        this.draw();
    }
    /**
     * Slices the rendered model based on the passed start and end line numbers.
     * (0, layerCount()) renders everything
     *
     * Note: Currently negative values are not allowed.
     *
     * @param {number} start the starting layer
     * @param {number} end the ending layer (excluding)
     */
    sliceLayer(start, end) {
        var _a, _b, _c;
        (_a = this.combinedLine) === null || _a === void 0 ? void 0 : _a.slice(start && ((_b = this.layerIndex[start]) === null || _b === void 0 ? void 0 : _b.start), end && ((_c = this.layerIndex[end]) === null || _c === void 0 ? void 0 : _c.end));
        this.draw();
    }
    /**
     * Retrieve the dom html canvas element where
     * the GCode viewer draws to.
     * @returns {HTMLCanvasElement}
     */
    element() {
        return this.renderer.domElement;
    }
    /**
     * disposes everything which is dispose able.
     * Call this always before destroying the instance.""
     */
    dispose() {
        var _a, _b, _c;
        (_a = this.cameraControl) === null || _a === void 0 ? void 0 : _a.dispose();
        (_b = this.combinedLine) === null || _b === void 0 ? void 0 : _b.dispose();
        (_c = this.texture) === null || _c === void 0 ? void 0 : _c.dispose();
    }
    /**
     * Get the amount of points in the model.
     *
     * @returns {number}
     */
    pointsCount() {
        var _a;
        return ((_a = this.combinedLine) === null || _a === void 0 ? void 0 : _a.pointsCount()) || 0;
    }
    /**
     * Get the amount of layers in the model.
     * This is an approximation which may be incorrect if the
     * nozzle moves downwards mid print.
     *
     * @returns {number}
     */
    layerCount() {
        return this.layerIndex.length || 0;
    }
    /**
     * This can be called when a resize of the canvas is needed.
     *
     * @param {number} width
     * @param {number} height
     */
    resize(width, height) {
        this.renderer.setSize(width, height);
        const rot = this.camera.rotation;
        const pos = this.camera.position;
        this.camera = this.newCamera(width, height);
        this.fitCamera();
        this.camera.rotation.copy(rot);
        this.camera.position.copy(pos);
        this.draw();
    }
}
exports.GCodeRenderer = GCodeRenderer;
